// A prefetched request is handed to the first caller with the same params and then forgotten, so
// later calls (refetch after a write, reopening a modal) always hit the network. The TTL bounds how
// stale an unclaimed prefetch can be when the same params are requested again later.
const PREFETCH_TTL_MS = 30_000

interface PrefetchEntry {
  promise: Promise<unknown>
  expiresAt: number
}

const entries = new Map<string, PrefetchEntry>()

const isFresh = (entry: PrefetchEntry) => entry.expiresAt > Date.now()

const buildKey = (name: string, params: object) =>
  `${name}:${JSON.stringify(Object.entries(params).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)))}`

export const createPrefetchable = <Params extends object, Result>(
  name: string,
  request: (params: Params) => Promise<Result>
) => ({
  fetch: (params: Params): Promise<Result> => {
    const key = buildKey(name, params)
    const entry = entries.get(key)
    entries.delete(key)
    return entry && isFresh(entry) ? (entry.promise as Promise<Result>) : request(params)
  },
  prefetch: (params: Params): void => {
    const key = buildKey(name, params)
    const existing = entries.get(key)
    if (existing && isFresh(existing)) return

    const promise = request(params)
    // Errors surface to whichever caller claims the promise; swallow here so an unclaimed prefetch
    // doesn't raise an unhandled rejection.
    promise.catch(() => undefined)
    entries.set(key, { promise, expiresAt: Date.now() + PREFETCH_TTL_MS })
  },
  discard: (params: Params): void => {
    entries.delete(buildKey(name, params))
  },
})

export const clearPrefetchCache = () => entries.clear()
